import { Flex, Icon, scale, Text } from "@capioweb/ui-components"
import React, { Fragment } from "react"
import styled from "styled-components"
import { resolveBmiDisplayValue } from "./utils/resolveBmiDisplayValue"

interface BmiBarProps {
	bmi?: number
	barTexts?: {
		bmi: string
		normalWeight: string
		obese: string
		overWeight: string
		underWeight: string
	}
}

const StyledContainer = styled(Flex)`
	width: 100%;
	height: 50px;
	border-radius: 1125px;
	background: linear-gradient(
		90deg,
		rgba(199, 213, 233, 1) 0%,
		rgba(203, 222, 218, 1) 25%,
		rgba(225, 235, 206, 1) 50%,
		rgba(236, 202, 168, 1) 75%,
		rgba(222, 140, 103, 1) 100%
	);
`

const BMILine = styled.div<{ left: number }>`
	height: 100%;
	width: 2px;
	position: absolute;
	left: ${({ left }) => left}%;
	background-color: rgb(33, 33, 33);
`

const BMINumber = styled(Text)<{ left: number }>`
	position: absolute;
	left: ${({ left }) => left}%;
	bottom: 0;
	margin-bottom: -${scale.px(8)};
	transform: translateX(-45%);
`

const BMIArrow = styled(Flex)<{ left: number }>`
	position: absolute;
	left: ${({ left }) => left}%;
	bottom: 0;
	margin-bottom: -${scale.px(4)};
	transform: translateX(-50%);
`

const TopLine = styled.div<{ left: number }>`
	height: 50%;
	width: 1px;
	position: absolute;
	left: ${({ left }) => left}%;
	background-color: rgb(134, 145, 142);
`

const BottomLine = styled.div<{ left: number }>`
	bottom: 0;
	height: 20%;
	width: 1px;
	position: absolute;
	left: ${({ left }) => left}%;
	background-color: rgb(134, 145, 142);
`

const BottomText = styled(Text)<{ left: number }>`
	text-align: center;
	bottom: 0;
	position: absolute;
	left: ${({ left }) => left}%;
	margin-bottom: -${scale.px(0.5)};
	transform: translateX(-50%);
`

const NameContainer = styled.div<{ left: number; width: number }>`
	position: absolute;
	align-items: center;
	height: 100%;
	left: ${({ left }) => left}%;
	display: flex;
	width: ${({ width }) => width}%;
	justify-content: center;
`

const steps = 20

const getLines = (): number[] => {
	const lines = []
	for (let i = 1; i < steps; i++) {
		lines.push(i * (100 / steps))
	}
	return lines
}

const getLabel = (percentage: number): string => {
	if (percentage === 25) {
		return "20"
	}
	if (percentage === 50) {
		return "25"
	}
	if (percentage === 75) {
		return "30"
	}
}

const calcPerFromBMI = (bmi: number) => {
	return 100 - (35 - bmi) * (100 / steps)
}

export const BmiBar: React.FC<BmiBarProps> = ({ barTexts, bmi }) => {
	const bmiIsInsideBar = bmi && bmi > 15.5 && bmi < 34.5
	const linePos = calcPerFromBMI(bmi)
	return (
		<Flex flexDirection={"column"} h={15} w={"100%"} overflowX={"hidden"}>
			<Flex position={"relative"}>
				<NameContainer left={0} width={17.5}>
					<Text mb={0}>{barTexts.underWeight}</Text>
				</NameContainer>
				<NameContainer left={17.5} width={32.5}>
					<Text mb={0}>{barTexts.normalWeight}</Text>
				</NameContainer>
				<NameContainer left={50} width={25}>
					<Text mb={0}>{barTexts.overWeight}</Text>
				</NameContainer>
				<NameContainer left={75} width={25}>
					<Text mb={0}>{barTexts.obese}</Text>
				</NameContainer>
				<Fragment>
					<TopLine left={17.5} />
					<BottomText left={17.5}>{18.5}</BottomText>
				</Fragment>
				{getLines().map((line) => {
					if (line === 50 || line === 75) {
						return (
							<Fragment key={line}>
								<TopLine left={line} />
								<BottomText left={line}>{getLabel(line)}</BottomText>
							</Fragment>
						)
					} else {
						return <BottomLine key={line} left={line} />
					}
				})}
				{bmiIsInsideBar && (
					<>
						<BMILine left={linePos} />
						<BMINumber left={linePos} variant={"headline-2"}>
							{barTexts.bmi} {resolveBmiDisplayValue(bmi)}
						</BMINumber>
						<BMIArrow left={linePos}>
							<Icon color={"navy"} icon="arrow-up" size={3} />
						</BMIArrow>
					</>
				)}
				<StyledContainer />
			</Flex>
		</Flex>
	)
}
